import {
    HttpClient,
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpHeaders,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { AssistContextService } from 'app/core/assist-context/assist-context.service';
import { Observable, catchError, throwError, tap } from 'rxjs';
import { environment } from "../../../environments/environment";
import { isNylasGrantAuthFailure } from './nylas-auth.util';

/**
 * Intercept
 *
 * @param req
 * @param next
 */

export class NylasApiService {
    private http = inject(HttpClient);
  
    makeNylasRequest(url: string, token: string): Observable<any> {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
      });
      return this.http.get(url, { headers });
    }
  }
  
  
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const assistContext = inject(AssistContextService);

    // Public access-code/token requests must not inherit a stale staff bearer
    // from shared devices. Callers opt out with this internal header; remove it
    // before the request leaves the browser.
    const skipAppAuth =
        req.headers.get('X-Skip-App-Auth') === '1' ||
        req.headers.get('X-Skip-App-Auth') === 'true';
    const cleanHeaders = skipAppAuth
        ? req.headers.delete('X-Skip-App-Auth').delete('Authorization')
        : req.headers;

    // Clone the request object
    let newReq = req.clone({ headers: cleanHeaders });
    
    // Skip auth for static assets (translation files, etc.)
    const isStaticAsset = req.url.includes('/i18n/') || 
                          req.url.includes('/assets/') || 
                          req.url.endsWith('.json') && !req.url.includes('/api/');

    // Skip the local GTX helper agent: it has its own token (sent as a
    // Bearer/X-GTX-Token header) and must not be overwritten with the app's
    // access token, nor trigger the 401 sign-out flow below.
    const gtxAgentBase = (environment as any)?.gtxAgent?.baseUrl;
    const isGtxAgent = !!gtxAgentBase && req.url.startsWith(gtxAgentBase);

    if (isStaticAsset || isGtxAgent) {
        return next(newReq);
    }
    
    let user: any;
    const userData = authService.localStorageUserData;
    if (userData && userData !== 'undefined' && userData !== 'null') {
        try {
            user = JSON.parse(userData);
        } catch (error) {
            console.error('Error parsing userData from localStorage:', error);
            localStorage.removeItem('userData');
        }
    }

    // Admin/Production company assist: use the company's Nylas grant when set.
    // Never fall back to the Admin grant while assisting (including the
    // persisted stub window before email_token is hydrated).
    const assistGrant = assistContext.nylasGrantId;
    const assisting = assistContext.isAssisting;
    const selfGrant =
        typeof user?.email_token === 'string' ? user.email_token.trim() : '';
    const effectiveGrant =
        (typeof assistGrant === 'string' && assistGrant.trim().length > 0)
            ? assistGrant.trim()
            : (assisting ? '' : selfGrant);
    
    // Mailbox traffic goes through Laravel (/api/admin/nylas/...). Legacy direct
    // api.us.nylas.com URLs are still detected so they fail closed without a key.
    const isNylasUrl =
        req.url.includes('/admin/nylas/v3/grants') ||
        req.url.includes('https://api.us.nylas.com/v3/grants');
    const invalidGrantKey =
        typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem('nylasGrantInvalid')
            : null;
    // Flag may be legacy "1" or a specific grant id that failed.
    const nylasGrantInvalid =
        invalidGrantKey === '1' ||
        (!!effectiveGrant && invalidGrantKey === effectiveGrant);
    const hasGrant = effectiveGrant.length > 0;

    // Email is optional — never rewrite/call Nylas with a missing or known-bad grant.
    // While assisting with a stub (no grant yet) or a company without mailbox,
    // skip rather than silently using the operator's mailbox.
    // Use throwError (not EMPTY) so forkJoin / UI loaders still settle and
    // can show "Failed to load" instead of hanging on an empty pane.
    if (isNylasUrl && (!hasGrant || nylasGrantInvalid)) {
        if (!nylasGrantInvalid) {
            console.warn(
                assisting
                    ? 'Skipping Nylas request: assisting without a company grant'
                    : 'Skipping Nylas request: no usable email_token (grant)',
            );
        }
        return throwError(
            () =>
                new HttpErrorResponse({
                    status: 0,
                    statusText: nylasGrantInvalid
                        ? 'Nylas grant paused'
                        : 'Nylas grant missing',
                    url: req.url,
                }),
        );
    }
  
    if (
        !skipAppAuth &&
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken)
    ) { 
        let headers = req.headers.set(
            'Authorization',
            'Bearer ' + authService.accessToken
        );
        // Opt out of company assist for self-scoped surfaces (e.g. chat
        // SMS label lookup). Callers set X-Assist-Skip: 1.
        const skipAssist =
            headers.get('X-Assist-Skip') === '1' ||
            headers.get('X-Assist-Skip') === 'true';
        if (skipAssist) {
            headers = headers.delete('X-Assist-Skip');
        } else if (
            assistContext.companyId != null &&
            assistContext.companyId !== ''
        ) {
            // Tell the API which company mailbox/book we're assisting as.
            // Required for the Nylas proxy to resolve the assisted grant.
            headers = headers.set(
                'X-Assist-Owner-Id',
                String(assistContext.companyId),
            );
        }
        newReq = req.clone({ headers });
    }

    // Response
    return next(newReq).pipe(
        tap(() => {
            // Successful Nylas call — clear pause for this grant so a
            // re-linked mailbox recovers without a full page reload.
            if (!isNylasUrl || !effectiveGrant) {
                return;
            }
            try {
                const invalid = sessionStorage.getItem('nylasGrantInvalid');
                if (invalid === effectiveGrant || invalid === '1') {
                    sessionStorage.removeItem('nylasGrantInvalid');
                }
            } catch {
                // ignore
            }
        }),
        catchError((error) => {
            const isNylasRequest =
                newReq.url.includes('/admin/nylas/') ||
                newReq.url.includes('nylas.com');

            // Pause email traffic only for real grant/auth failures.
            // Ordinary resource 404s (missing draft/message/label) must NOT
            // trip the circuit breaker — that left the inbox list visible
            // while every thread body silently failed to load.
            if (
                error instanceof HttpErrorResponse &&
                isNylasRequest &&
                isNylasGrantAuthFailure(error)
            ) {
                // Laravel auth 401 on the proxy should sign the user out,
                // not pause the mailbox forever.
                const body = error.error;
                const laravelUnauth =
                    error.status === 401 &&
                    (body?.error === 'unauthenticated' ||
                        body?.error === 'token_expired' ||
                        String(body?.message || '')
                            .toLowerCase()
                            .includes('unauthenticated'));
                if (!laravelUnauth) {
                    try {
                        // Key by grant so switching company assist can retry a different mailbox.
                        sessionStorage.setItem(
                            'nylasGrantInvalid',
                            effectiveGrant || '1',
                        );
                    } catch {
                        // ignore
                    }
                    console.warn(
                        `Nylas grant rejected (${error.status}) — pausing further Nylas requests for this grant:`,
                        newReq.url
                    );
                    return throwError(() => error);
                }
            }

            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // Check if we're on the resource-view route (public route with access code)
                const currentUrl = router.url || '';
                const isResourceViewRoute =
                    currentUrl.includes('/resource-view') ||
                    currentUrl.includes('/photo-sample-review');
                
                // If on resource-view route, don't sign out - let the component handle the error
                if (isResourceViewRoute) {
                    console.log('401 on resource-view route - allowing component to handle error');
                    return throwError(() => error);
                }
                
                // Only logout for main API calls, not external services like Nylas
                // Exclude authentication endpoints - let them handle their own 401s
                const isAuthEndpoint = newReq.url.includes('/api-login') || 
                                       newReq.url.includes('/login') ||
                                       newReq.url.includes('/sign-in');
                // Proxied Nylas grant failures are handled above; Laravel
                // unauthenticated on /admin/nylas/ still counts as a main API 401.
                const isExternalNylas = newReq.url.includes('nylas.com');
                const isMainApiCall = newReq.url.includes('/api/') && !isExternalNylas;
                const isBackendApiCall = newReq.url.startsWith('/api') && !isExternalNylas;

                if ((isMainApiCall || isBackendApiCall) && !isAuthEndpoint) {
                    // SIGN-OUT RACE GUARD
                    // ------------------------------------------------
                    // When the user just clicked Sign Out, the auth
                    // service flips `isSigningOut = true` BEFORE
                    // clearing the access token. Any polling request
                    // (orders count, mailbox webhooks, presence,
                    // chat heartbeat, etc.) that was already in
                    // flight will land here with a 401 because the
                    // token is now gone.
                    //
                    // Triggering signOut + location.reload() on those
                    // orphan 401s produces the classic "logs out and
                    // the page refreshes twice" UX bug: first the
                    // intentional router navigation to /sign-in, then
                    // a full reload from this branch. Swallow the
                    // 401 quietly so the sign-out flow stays single
                    // refresh.
                    if (authService && authService.isSigningOut) {
                        return throwError(() => error);
                    }

                    const errorBody = error.error;
                    const errorMessage = errorBody?.message || '';
                    const errorCode = errorBody?.error || '';

                    console.warn('401 Unauthorized from main API:', {
                        url: newReq.url,
                        error: errorCode,
                        message: errorMessage
                    });

                    if (
                        errorCode === 'token_expired' ||
                        errorCode === 'unauthenticated' ||
                        errorMessage.includes('Token signature mismatch') ||
                        errorMessage.includes('access denied') ||
                        errorMessage.includes('authorization server denied')
                    ) {
                        console.warn('Invalid or expired token detected, clearing auth state');
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('userData');
                        localStorage.removeItem('userData_tradelist');
                    }

                    // Defensive check: ensure authService.signOut exists before calling
                    // This can fail during service initialization (circular dependency edge case)
                    if (authService && typeof authService.signOut === 'function') {
                        authService.signOut().subscribe({
                            next: () => {
                                location.reload();
                            },
                            error: (signOutError) => {
                                console.error('Error during sign out:', signOutError);
                                location.reload();
                            }
                        });
                    } else {
                        console.warn('AuthService not fully initialized, redirecting to sign-in');
                        location.href = '/sign-in';
                    }

                    return throwError(() => error);
                } else {
                    console.warn('401 Unauthorized from external service, ignoring:', newReq.url);
                }
            }

            return throwError(() => error);
        })
    );


     
};
